<template>
  <div id="feature-type-edit">
    <div
      :class="{ active: loading || importingFile }"
      class="ui inverted dimmer"
    >
      <div class="ui loader" />
    </div>

    <div v-if="project" class="ft-create">
      <nav class="ft-crumb" aria-label="Fil d'Ariane">
        <router-link :to="{ name: 'project_detail', params: { slug } }">
          {{ project.title }}
        </router-link>
        <span aria-hidden="true"> › </span>
        <span>{{ crumbLabel }}</span>
      </nav>

      <h1>{{ pageTitle }}</h1>

      <form id="form-type-edit" @submit.prevent="submitFeatureType">
        <FeatureTypeStructureSource
          v-if="action === 'create'"
          :source="source"
          :show-catalog="Boolean(IDGO)"
          :catalog-name="catalogName"
          :file-name="importedFileName"
          :file-error="fileError"
          :importing="importingFile"
          :file-summary="fileSummary"
          :import-features.sync="importFeatures"
          @manual="selectManual"
          @file-intent="source = 'file'"
          @file="onStructureFile"
          @catalog="selectCatalog"
          @load-catalog="onLoadCatalog"
        />

        <section class="ft-section">
          <h2>{{ action === "create" ? "2" : "1" }}. Informations générales</h2>

          <div class="ft-general__grid">
            <div class="ft-field">
              <label :for="form.title.id_for_label">
                <span class="req">*</span> Nom du jeu de données
              </label>
              <input
                :id="form.title.id_for_label"
                v-model="form.title.value"
                type="text"
                required
                :maxlength="form.title.field.max_length"
                :name="form.title.html_name"
                @blur="updateStore"
              />
              <ul id="errorlist" class="errorlist">
                <li v-for="err in form.title.errors" :key="err">
                  {{ err }}
                </li>
              </ul>
            </div>

            <div id="geometry-type" class="ft-field">
              <label for="geom_type">
                <span class="req">*</span> Type de géométries
              </label>
              <select
                id="geom_type"
                v-model="form.geom_type.value"
                required
                @change="updateStore"
              >
                <option
                  v-for="opt in geomTypeChoices"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.name }}
                </option>
              </select>
            </div>
          </div>

          <label class="ft-check">
            <input
              :id="form.title_optional.html_name"
              v-model="form.title_optional.value"
              type="checkbox"
              :name="form.title_optional.html_name"
            />
            <span>
              <strong>Titre de signalement optionnel</strong>
              <small
                >Le titre du signalement par le contributeur n'est pas
                obligatoire. Attention !! Un titre par défaut sera mis de cette
                forme : "22061ced-43..."</small
              >
            </span>
          </label>

          <label class="ft-check">
            <input
              :id="form.enable_key_doc_notif.html_name"
              v-model="form.enable_key_doc_notif.value"
              type="checkbox"
              :name="form.enable_key_doc_notif.html_name"
            />
            <span>
              <strong>Activer la notification de pièces jointes</strong>
              <small
                >Permet à l'administrateur d'être notifié lorsqu'une photo a été
                mise par le contributeur</small
              >
            </span>
          </label>

          <label class="ft-check">
            <input
              :id="form.disable_notification.html_name"
              v-model="form.disable_notification.value"
              type="checkbox"
              :name="form.disable_notification.html_name"
            />
            <span>
              <strong>Désactiver les notifications</strong>
              <small
                >Désactive les notifications des ajouts des contributeurs sur ce
                type de signalement</small
              >
            </span>
          </label>
        </section>

        <section class="ft-section">
          <h2>{{ action === "create" ? "3" : "2" }}. Champs personnalisés</h2>
          <p class="ft-section__intro">
            Le bouton « Règles » de chaque ligne ouvre l'activation
            conditionnelle et les valeurs forcées. Glissez les lignes pour
            changer l'ordre dans le formulaire.
          </p>
          <p
            v-if="prefilledFieldCount"
            class="ft-prefill"
            data-test="prefilled-fields-message"
          >
            {{ prefillMessage }}
          </p>

          <table :key="tableRenderKey" ref="fieldsTable" class="ft-fields">
            <thead>
              <tr>
                <th class="ft-fields__handle" />
                <th>Libellé affiché</th>
                <th>Nom technique</th>
                <th>Type</th>
                <th>Obligatoire</th>
                <th>Actions</th>
              </tr>
            </thead>
            <FeatureTypeCustomForm
              v-for="customForm in customForms"
              :key="customForm.dataKey"
              ref="customForms"
              :custom-form="customForm"
              :selected-color-style="form.colors_style.value.custom_field_name"
              @update="updateColorsStyle($event)"
            />
          </table>

          <button
            id="add-field"
            type="button"
            class="ft-add-field"
            @click="addCustomForm()"
          >
            <i class="plus icon" aria-hidden="true" />
            Ajouter un nouveau champ
          </button>
        </section>

        <div class="ft-actions">
          <button
            type="button"
            class="ft-actions__cancel"
            data-test="cancel-feature-type"
            @click="cancel"
          >
            Annuler
          </button>
          <button
            id="send-feature_type"
            type="submit"
            class="ft-actions__primary"
            :disabled="loading"
            data-test="create-feature-type"
          >
            {{ submitLabel }}
          </button>
        </div>
      </form>
    </div>

    <div
      v-if="isFileSizeModalOpen"
      class="ft-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="file-size-title"
    >
      <div class="ft-modal__card">
        <h3 id="file-size-title">Fichier trop grand</h3>
        <p>
          Impossible de créer un type de signalement à partir d'un fichier de
          plus de 100 Mo (celui importé fait {{ rejectedFileSize }} Mo).
        </p>
        <button
          type="button"
          class="ft-actions__primary"
          @click="isFileSizeModalOpen = false"
        >
          Fermer
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { csv } from "csvtojson";
import Sortable from "sortablejs";
import { mapGetters, mapState, mapMutations, mapActions } from "vuex";

import FeatureTypeCustomForm from "@/components/FeatureType/FeatureTypeCustomForm.vue";
import FeatureTypeStructureSource from "@/components/FeatureType/FeatureTypeStructureSource.vue";
import { fileConvertSizeToMo, determineDelimiter } from "@/assets/js/utils";
import miscAPI from "@/services/misc-api";
import { transformProperties, reservedKeywords } from "@/utils";

export default {
  name: "FeatureTypeEdit",

  components: {
    FeatureTypeCustomForm,
    FeatureTypeStructureSource,
  },

  props: {
    geojson: {
      type: Object,
      default: null,
    },
    csv: {
      type: Array,
      default: null,
    },
    json: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      importingFile: false,
      action: "create",
      source: "manual",
      importedGeojson: this.geojson,
      importedCsv: this.csv,
      importedJson: this.json,
      importedFileName: "",
      fileError: "",
      fileSummary: null,
      importFeatures: false,
      structureOrigin: null,
      prefilledFieldCount: 0,
      isFileSizeModalOpen: false,
      rejectedFileSize: "",
      fieldsSortable: null,
      tableRenderKey: 0,
      dataKey: 0,
      csvFields: null,
      geomTypeChoices: [
        { value: "linestring", name: "Ligne" },
        { value: "point", name: "Point" },
        { value: "polygon", name: "Polygone" },
        { value: "none", name: "Aucune : signalement non géographique" },
      ],
      form: {
        colors_style: {
          fields: [],
          value: {
            colors: {},
            custom_field_name: "",
          },
        },
        color: {
          id_for_label: "couleur",
          label: "Couleur",
          field: {
            max_length: 128, // ! Vérifier la valeur dans django
          },
          html_name: "couleur",
          value: "#000000",
        },
        title: {
          errors: [],
          id_for_label: "title",
          label: "Titre",
          field: {
            max_length: 128,
          },
          html_name: "title",
          value: null,
        },
        title_optional: {
          errors: null,
          id_for_label: "title_optional",
          html_name: "title_optional",
          label: "Titre du signalement optionnel",
          value: false,
        },
        enable_key_doc_notif: {
          errors: null,
          id_for_label: "enable_key_doc_notif",
          html_name: "enable_key_doc_notif",
          label: "Activer la notification de publication de pièces jointes",
          value: false,
        },
        disable_notification: {
          errors: null,
          id_for_label: "disable_notification",
          html_name: "disable_notification",
          label: "Désactiver les notifications",
          value: false,
        },
        geom_type: {
          id_for_label: "geom_type",
          label: "Type de géométrie",
          field: {
            max_length: 128,
          },
          html_name: "geom_type",
          value: "point",
        },
      },
      slug: this.$route.params.slug,
    };
  },

  computed: {
    ...mapState(["configuration"]),
    ...mapState("projects", ["project"]),
    ...mapState("feature-type", [
      "customForms",
      "colorsStyleList",
      "fileToImport",
    ]),
    ...mapGetters("feature-type", ["feature_type"]),

    catalogName() {
      return this.configuration && this.configuration.VUE_APP_CATALOG_NAME;
    },
    IDGO() {
      return this.configuration && this.configuration.VUE_APP_IDGO;
    },
    pageTitle() {
      if (this.action === "edit") return "Éditer le type de signalement";
      if (this.action === "duplicate")
        return "Dupliquer le type de signalement";
      return "Créer un nouveau type de signalement";
    },
    crumbLabel() {
      if (this.action === "create") return "Nouveau type de signalement";
      if (this.feature_type && this.feature_type.title)
        return this.feature_type.title;
      return "Type de signalement";
    },
    submitLabel() {
      if (this.action === "edit") return "Enregistrer le type de signalement";
      return "Créer le type de signalement";
    },
    prefillMessage() {
      const count = this.prefilledFieldCount;
      if (count > 1) {
        return `${count} champs ont été préremplis automatiquement. Veuillez les vérifier avant de valider.`;
      }
      return "1 champ a été prérempli automatiquement. Veuillez le vérifier avant de valider.";
    },
    hasImportedFeatures() {
      const geojson = this.importedGeojson;
      if (geojson && geojson.features && geojson.features.length) return true;
      if (this.importedCsv && this.importedCsv.length) return true;
      if (this.importedJson && this.importedJson.length) return true;
      return false;
    },
    selected_colors_style: {
      get() {
        const name = this.form.colors_style.value.custom_field_name;
        const customField = this.customForms.find((el) => el.name === name);
        return customField && customField.length !== 0
          ? customField.label
          : name;
      },
      set(newValue) {
        //* update only if different than custom_form
        if (newValue.value !== this.form.colors_style.value.custom_field_name) {
          //* get back values from original feature_type
          if (
            this.feature_type && //* if the feature_type exists already
            newValue.value === this.feature_type.colors_style.custom_field_name
          ) {
            this.form.colors_style.value = this.feature_type.colors_style;
          } else {
            const newColorsStyle = {
              colors: newValue.options.reduce((obj, key) => {
                obj[key] = "#000000";
                return obj;
              }, {}),
            };
            this.form.colors_style.value = newColorsStyle;
            this.updateStore();
          }
        }
        this.form.colors_style.value.custom_field_name = newValue.value;
        this.form = { ...this.form }; //! force reactivity to update custom_field_name
      },
    },
  },

  watch: {
    feature_type(newValue) {
      if (newValue) {
        this.fillFormData(newValue);
      }
    },
    project(value) {
      if (value) {
        this.$nextTick(() => this.initFieldsSortable());
      }
    },
    customForms(newValue, oldValue) {
      if (newValue !== oldValue) {
        // Retrieve custom_field_name; returns undefined if colors_style.value is null/undefined
        const customFieldName = this.form.colors_style.value?.custom_field_name;

        // Determine if a custom field with the given name exists in customForms
        // 'some' returns true if any element matches the condition
        const customFieldExists =
          customFieldName &&
          this.customForms.some((el) => el.name === customFieldName);

        // Reset colors_style if no corresponding custom field is found
        if (!customFieldExists) {
          this.form.colors_style.value = { colors: {}, custom_field_name: "" };
        }
      }
    },
  },

  created() {
    if (!this.project) {
      this.$store.dispatch("projects/GET_PROJECT", this.$route.params.slug);
      this.$store.dispatch(
        "projects/GET_PROJECT_INFO",
        this.$route.params.slug,
      );
    }
    this.SET_CURRENT_FEATURE_TYPE_SLUG(this.$route.params.slug_type_signal);

    this.definePageType();
    if (this.fileToImport && this.fileToImport.name) {
      this.importedFileName = this.fileToImport.name;
    }
    if (this.importedGeojson) {
      this.source =
        this.$route.params.type === "external-geojson" ? "catalog" : "file";
    } else if (this.importedCsv || this.importedJson) {
      this.source = "file";
    }
  },

  mounted() {
    if (this.action === "edit" || this.action === "duplicate") {
      if (this.feature_type) {
        //* add datas from store to state to avoid mutating directly store with v-model (not good practice), could have used computed with getter and setter as well
        this.fillFormData(this.feature_type);
      }
      if (this.action === "duplicate") {
        //* replace original name with new default title
        this.form.title.value += ` (Copie-${new Date()
          .toLocaleString()
          .slice(0, -3)
          .replace(",", "")})`;
        this.updateStore(); // * initialize form in store in case this.form would not be modified
      }
    }
    if (this.importedGeojson) {
      this.ensureMultiGeomChoices();
      this.importGeoJsonFeatureType();
    }
    if (this.importedCsv) {
      this.importCSVFeatureType();
    }
    if (this.importedJson) {
      this.importJsonFeatureType();
    }
    if (this.importedGeojson || this.importedCsv || this.importedJson) {
      this.structureOrigin = this.source === "catalog" ? "catalog" : "file";
      this.summarizeImport();
      this.prefilledFieldCount = this.customForms.length;
    }
    this.$nextTick(() => this.initFieldsSortable());
  },
  beforeDestroy() {
    if (this.fieldsSortable) {
      this.fieldsSortable.destroy();
    }
    this.EMPTY_FORM();
    this.EMPTY_CUSTOM_FORMS();
    this.SET_FILE_TO_IMPORT(null);
  },

  methods: {
    ...mapMutations(["DISPLAY_MESSAGE"]),
    ...mapMutations("feature-type", [
      "ADD_CUSTOM_FORM",
      "EMPTY_FORM",
      "EMPTY_CUSTOM_FORMS",
      "SET_CUSTOM_FORMS",
      "UPDATE_FORM",
      "SET_FILE_TO_IMPORT",
      "SET_CURRENT_FEATURE_TYPE_SLUG",
    ]),
    ...mapActions("feature-type", [
      "SEND_FEATURE_TYPE",
      "SEND_FEATURES_FROM_GEOJSON",
    ]),

    selectManual() {
      if (
        this.structureOrigin === "file" ||
        this.structureOrigin === "catalog"
      ) {
        this.importedGeojson = null;
        this.importedCsv = null;
        this.importedJson = null;
        this.importedFileName = "";
        this.fileSummary = null;
        this.importFeatures = false;
        this.prefilledFieldCount = 0;
        this.fileError = "";
        this.structureOrigin = null;
        this.SET_FILE_TO_IMPORT(null);
        this.EMPTY_CUSTOM_FORMS();
        this.dataKey = 0;
        this.form.geom_type.value = "point";
        this.updateStore();
      }
      this.source = "manual";
    },

    selectCatalog() {
      this.source = "catalog";
    },

    onLoadCatalog({ resource, withFeatures }) {
      if (!resource) {
        return;
      }
      this.importingFile = true;
      this.fileError = "";
      const queryParams = `typename=${resource.layer}&organization_slug=${resource.organization_slug}`;
      miscAPI
        .getExternalGeojson(queryParams)
        .then((data) => {
          if (!data) {
            this.DISPLAY_MESSAGE({
              comment: "Impossible de charger cette ressource du catalogue.",
              level: "negative",
            });
            return;
          }
          this.SET_FILE_TO_IMPORT(null);
          this.applyFileStructure({ geojson: data, origin: "catalog" });
          this.importFeatures = Boolean(withFeatures);
          const resourceName = resource.resource_name || resource.dataset_name;
          if (resourceName) {
            this.form.title.value = resourceName;
            this.updateStore();
          }
        })
        .catch((err) => {
          console.error(err);
          this.DISPLAY_MESSAGE({
            comment: "Impossible de charger la ressource du catalogue.",
            level: "negative",
          });
        })
        .finally(() => {
          this.importingFile = false;
        });
    },

    submitFeatureType() {
      if (this.importFeatures && this.hasImportedFeatures) {
        this.postFeatureTypeThenFeatures();
      } else {
        this.sendFeatureType();
      }
    },

    cancel() {
      this.$router.push({
        name: "project_detail",
        params: { slug: this.slug },
      });
    },

    ensureMultiGeomChoices() {
      const extras = [
        { value: "multilinestring", name: "Multiligne" },
        { value: "multipoint", name: "Multipoint" },
        { value: "multipolygon", name: "Multipolygone" },
      ];
      extras.forEach((opt) => {
        if (!this.geomTypeChoices.some((el) => el.value === opt.value)) {
          this.geomTypeChoices.push(opt);
        }
      });
    },

    onStructureFile(event) {
      const files = event.target.files || event.dataTransfer.files;
      if (!files || !files.length) {
        return;
      }
      const file = files[0];
      event.target.value = "";
      const sizeMo = fileConvertSizeToMo(file.size);
      if (parseFloat(sizeMo) > 100) {
        this.rejectedFileSize = sizeMo;
        this.isFileSizeModalOpen = true;
        return;
      }
      this.importingFile = true;
      this.fileError = "";
      this.importedFileName = file.name;
      this.SET_FILE_TO_IMPORT(file);
      const extension = file.name.split(".").pop().toLowerCase();
      const reader = new FileReader();
      reader.onload = () => {
        try {
          if (extension === "csv") {
            this.parseCsvFile(reader.result, file.name);
            return;
          }
          const parsed = JSON.parse(reader.result);
          if (extension === "geojson" || (parsed && !Array.isArray(parsed))) {
            this.applyFileStructure({ geojson: parsed });
          } else if (Array.isArray(parsed)) {
            this.applyFileStructure({ json: parsed });
          } else {
            this.fileError = "Format de fichier non reconnu.";
          }
          this.importingFile = false;
        } catch (err) {
          console.error(err);
          this.fileError = "Impossible de lire ce fichier.";
          this.importingFile = false;
        }
      };
      reader.onerror = () => {
        this.fileError = "Impossible de lire ce fichier.";
        this.importingFile = false;
      };
      reader.readAsText(file);
    },

    parseCsvFile(text, fileName) {
      const delimiter = determineDelimiter(text);
      if (!delimiter) {
        this.fileError = `Le fichier ${fileName} n'est pas formaté correctement.`;
        this.importingFile = false;
        return;
      }
      csv({ delimiter })
        .fromString(text)
        .then((jsonObj) => {
          this.applyFileStructure({ csv: jsonObj });
          this.importingFile = false;
        })
        .catch((err) => {
          console.error(err);
          this.fileError = `Le fichier ${fileName} n'est pas formaté correctement.`;
          this.importingFile = false;
        });
    },

    applyFileStructure({
      geojson = null,
      csv: csvRows = null,
      json = null,
      origin = "file",
    }) {
      const wasFile = this.structureOrigin === "file";
      this.source = origin;
      this.structureOrigin = origin;
      if (origin === "file" && !wasFile) {
        this.importFeatures = false;
      }
      this.importedGeojson = geojson;
      this.importedCsv = csvRows;
      this.importedJson = json;
      this.EMPTY_CUSTOM_FORMS();
      this.dataKey = 0;
      if (geojson) {
        this.ensureMultiGeomChoices();
        this.importGeoJsonFeatureType();
      } else if (csvRows) {
        this.importCSVFeatureType();
      } else if (json) {
        this.importJsonFeatureType();
      }
      this.summarizeImport();
      this.prefilledFieldCount = this.customForms.length;
    },

    countPropertyFields(entries) {
      const keys = new Set();
      (entries || []).forEach((properties) => {
        if (!properties || typeof properties !== "object") {
          return;
        }
        Object.keys(properties).forEach((key) => {
          if (!reservedKeywords.includes(key)) {
            keys.add(key);
          }
        });
      });
      return keys.size;
    },

    summarizeImport() {
      if (this.structureOrigin !== "file") {
        this.fileSummary = null;
        return;
      }
      let featureCount = 0;
      let entries = [];
      if (
        this.importedGeojson &&
        Array.isArray(this.importedGeojson.features)
      ) {
        featureCount = this.importedGeojson.features.length;
        entries = this.importedGeojson.features.map(
          (feature) => feature.properties,
        );
      } else if (Array.isArray(this.importedCsv)) {
        featureCount = this.importedCsv.length;
        entries = this.importedCsv;
      } else if (Array.isArray(this.importedJson)) {
        featureCount = this.importedJson.length;
        entries = this.importedJson;
      }
      const geometry = this.geomTypeChoices.find(
        (el) => el.value === this.form.geom_type.value,
      );
      const geometryLabel =
        this.form.geom_type.value === "none"
          ? "aucune"
          : geometry
          ? geometry.name.toLowerCase()
          : this.form.geom_type.value;
      this.fileSummary = {
        featureCount,
        fieldCount: this.countPropertyFields(entries),
        geometryLabel,
      };
    },

    initFieldsSortable() {
      if (this.fieldsSortable || !this.$refs.fieldsTable) {
        return;
      }
      this.fieldsSortable = new Sortable(this.$refs.fieldsTable, {
        animation: 150,
        handle: ".ft-field-row__handle",
        draggable: "tbody.ft-field-row",
        ghostClass: "ft-field-row--ghost",
        onEnd: this.reorderCustomForms,
      });
    },

    reorderCustomForms(evt) {
      const oldIndex =
        evt.oldDraggableIndex != null ? evt.oldDraggableIndex : evt.oldIndex;
      const newIndex =
        evt.newDraggableIndex != null ? evt.newDraggableIndex : evt.newIndex;
      if (oldIndex === newIndex || oldIndex == null || newIndex == null) {
        return;
      }
      const forms = this.customForms.slice();
      const moved = forms.splice(oldIndex, 1)[0];
      forms.splice(newIndex, 0, moved);
      this.SET_CUSTOM_FORMS(forms);
      if (this.fieldsSortable) {
        this.fieldsSortable.destroy();
        this.fieldsSortable = null;
      }
      this.tableRenderKey += 1;
      this.$nextTick(() => {
        this.initFieldsSortable();
        const rows = this.$refs.customForms || [];
        const list = Array.isArray(rows) ? rows : [rows];
        list.forEach((comp, index) => {
          if (comp && typeof comp.setPosition === "function") {
            comp.setPosition(index);
          }
        });
      });
    },

    definePageType() {
      if (this.$router.history.current.name === "ajouter-type-signalement") {
        this.action = "create";
      } else if (
        this.$router.history.current.name === "editer-type-signalement"
      ) {
        this.action = "edit";
      } else if (
        this.$router.history.current.name === "dupliquer-type-signalement"
      ) {
        this.action = "duplicate";
      }
    },

    addCustomForm(customForm) {
      if (
        customForm &&
        this.customForms.some((cf) => cf.name === customForm.name)
      ) {
        //* abort if customForm already exists (because watcher can update again)
        return;
      }
      this.dataKey += 1; // * increment counter for key in v-for
      let newCustomForm = {
        dataKey: this.dataKey,
      };
      if (customForm) {
        //* if adding an existing customForm -> add its property to newCustomForm containing only dataKey
        newCustomForm = { ...newCustomForm, ...customForm };
      }
      this.ADD_CUSTOM_FORM(newCustomForm); // * create an object with the counter in store
    },

    fillFormData(formData) {
      for (const el in formData) {
        // * find feature_type and fill form values
        if (this.form[el]) {
          this.form[el].value = formData[el];
        }
      }
      //! add custom fields using ONLY this function, incrementing dataKey for Vue to correctly update components
      [...formData.customfield_set].forEach((el) => this.addCustomForm(el));
      this.updateStore();
    },

    setColorStyles(event) {
      const { name, value } = event.target;
      this.form.colors_style.value.colors[name] = value;
    },

    updateColorsStyle(newOptions) {
      const optionNames = Object.keys(this.form.colors_style.value.colors);
      //* if new value added
      if (newOptions.length > optionNames.length) {
        for (const key of newOptions) {
          if (key && !optionNames.includes(key)) {
            //* check if key is not en empty string
            this.form.colors_style.value.colors[key] = "#000000"; //* add new entry
          }
        }
        //* if modified or deleted
      } else {
        const modifiedColorStyle = {};
        for (const [index, key] of newOptions.entries()) {
          //* if no key then item will disappear (deleted)
          if (key) {
            const values = Object.values(this.form.colors_style.value.colors);
            modifiedColorStyle[key] = values[index]; //* overide key and get previous value from previous colors style
          }
        }
        this.form.colors_style.value.colors = modifiedColorStyle;
      }
    },

    updateStore() {
      this.UPDATE_FORM({
        color: this.form.color,
        title: this.form.title,
        title_optional: this.form.title_optional,
        enable_key_doc_notif: this.form.enable_key_doc_notif,
        disable_notification: this.form.disable_notification,
        geom_type: this.form.geom_type,
        colors_style: this.form.colors_style,
      });
    },

    checkCustomForms() {
      let is_valid = true;
      if (this.$refs.customForms) {
        for (const customForm of this.$refs.customForms) {
          if (customForm.checkCustomForm() === false) {
            is_valid = false;
          }
        }
      }
      return is_valid; //* fallback if all customForms returned true
    },

    checkForms() {
      if (this.form.title.value) {
        this.form.title.errors = [];
        return this.checkCustomForms(); //* if customForms are ok, validate, if get out function
      } else if (
        !this.form.title.errors.includes("Veuillez compléter ce champ.")
      ) {
        this.form.title.errors.push("Veuillez compléter ce champ.");
        document
          .getElementById("errorlist")
          .scrollIntoView({ block: "end", inline: "nearest" });
      }
      return false;
    },

    goBackToProject(message) {
      this.$router.push({
        name: "project_detail",
        params: {
          slug: this.slug,
          message,
        },
      });
    },

    sendFeatureType() {
      this.updateStore();
      // * si édition d'une feature_type déja existante, faire un put
      const requestType = this.action === "edit" ? "put" : "post";
      if (this.checkForms()) {
        this.SEND_FEATURE_TYPE(requestType).then((response) => {
          const { status, data } = response;
          if (status === 200) {
            this.goBackToProject({
              comment: "Le type de signalement a été mis à jour",
              level: "positive",
            });
          } else if (status === 201) {
            this.goBackToProject({
              comment: "Le nouveau type de signalement a été créé",
              level: "positive",
            });
          } else {
            let comment =
              "Une erreur est survenue lors de l'import du type de signalement";
            if (data.customfield_set) {
              let errors = data.customfield_set.find((el) => el.options);
              if (errors.options) {
                let customFieldError = errors.options[0];
                if (customFieldError)
                  comment = customFieldError[0].replace(
                    "ce champ",
                    "chaque option de champ personnalisé",
                  );
              }
            }
            this.DISPLAY_MESSAGE({
              comment,
              level: "negative",
            });
          }
        });
      }
    },

    postGeojsonFeatures(feature_type_slug) {
      this.SEND_FEATURES_FROM_GEOJSON({
        slug: this.slug,
        feature_type_slug,
        geojson: this.importedGeojson || this.importedJson,
      })
        .then((response) => {
          if (response && response.status === 200) {
            this.goBackToProject({
              comment:
                "Le nouveau type de signalement a été créé. L'import des signalements est en cours",
              level: "positive",
            });
          } else {
            this.DISPLAY_MESSAGE({
              comment: `Une erreur est survenue lors de l'import de signalements.\n ${response.data.detail}`,
              level: "negative",
            });
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    postCSVFeatures(feature_type_slug) {
      this.$store
        .dispatch("feature-type/SEND_FEATURES_FROM_CSV", {
          slug: this.slug,
          feature_type_slug,
          csv: this.importedCsv,
        })
        .then((response) => {
          if (response && response.status === 200) {
            this.goBackToProject({
              comment:
                "Le nouveau type de signalement a été créé. Import des signalements est en cours",
              level: "positive",
            });
          } else {
            this.DISPLAY_MESSAGE({
              comment: `Une erreur est survenue lors de l'import de signalements.\n ${response.data.detail}`,
              level: "negative",
            });
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    async postFeatureTypeThenFeatures() {
      this.updateStore();
      const requestType = this.action === "edit" ? "put" : "post";
      if (this.checkForms()) {
        this.loading = true;
        await this.$store
          .dispatch("feature-type/SEND_FEATURE_TYPE", requestType)
          .then(({ feature_type_slug }) => {
            if (feature_type_slug) {
              if (this.importedGeojson || this.importedJson) {
                this.postGeojsonFeatures(feature_type_slug);
              } else if (this.importedCsv) {
                this.postCSVFeatures(feature_type_slug);
              }
            } else {
              this.loading = false;
            }
          })
          .catch(() => {
            this.loading = false;
          });
      }
    },

    // ****** Methodes for geojson import ****** //
    toNewFeatureType() {
      this.$router.push({
        name: "ajouter-type-signalement",
        params: { geojson: this.jsonDict },
      });
    },

    /**
     * Builds custom form fields based on the properties of data entries.
     *
     * This function iterates through a subset of data entries (such as rows from a CSV, JSON objects, or GeoJSON features)
     * to determine the most appropriate type for each field. It tracks confirmed types to avoid redundant checks and
     * stops processing a field once its type is definitively determined. If a field is initially detected as a 'char',
     * it remains as 'char' unless a multiline text ('text') is detected later. The function prioritizes the detection
     * of definitive types (like 'text', 'boolean', 'integer') and updates the form with the confirmed types.
     *
     * @param {Array} propertiesList - An array of data entries, where each entry is an object representing a set of properties.
     */

    buildCustomForm(propertiesList) {
      const confirmedTypes = {}; // Store confirmed types for each field
      const detectedAsChar = {}; // Track fields initially detected as 'char'

      // Iterate over each row or feature in the subset
      propertiesList.forEach((properties) => {
        for (const [key, val] of Object.entries(properties)) {
          if (!reservedKeywords.includes(key)) {
            // If the type for this field has already been confirmed as something other than 'char', skip it
            if (confirmedTypes[key] && confirmedTypes[key] !== "char") {
              continue;
            }

            // Determine the type of the current value
            const detectedType = transformProperties(val);

            if (detectedType === "text") {
              // Once 'text' (multiline) is detected, confirm it immediately
              confirmedTypes[key] = "text";
            } else if (!confirmedTypes[key] && detectedType !== "char") {
              // If a type is detected that is not 'char' and not yet confirmed, confirm it
              confirmedTypes[key] = detectedType;
            } else if (!confirmedTypes[key]) {
              // If this field hasn't been confirmed yet, initialize it as 'char'
              confirmedTypes[key] = "char";
              detectedAsChar[key] = true;
            } else if (detectedAsChar[key] && detectedType !== "char") {
              // If a field was initially detected as 'char' but now has a different type, update it
              confirmedTypes[key] = detectedType;
              delete detectedAsChar[key]; // Remove from 'char' tracking once updated
            }
          }
        }
      });

      // Build custom forms using the confirmed types
      for (const [key, confirmedType] of Object.entries(confirmedTypes)) {
        const customForm = {
          label: { value: key || "" },
          name: { value: key || "" },
          position: this.dataKey, // use dataKey already incremented by addCustomForm
          field_type: { value: confirmedType }, // use the confirmed type
          options: { value: [] }, // not available in export
        };
        this.addCustomForm(customForm);
      }
    },

    setTitleFromFile() {
      if (this.fileToImport && this.fileToImport.name) {
        this.form.title.value = this.fileToImport.name.split(".")[0]; // * use the filename as title by default
      } else {
        //* case when the data comes from datasud catalog
        // * use the typename as title by default
        this.form.title.value =
          (this.importedGeojson && this.importedGeojson.name) ||
          (this.importedCsv && this.importedCsv.name) ||
          (this.importedJson && this.importedJson.name);
      }
    },

    importGeoJsonFeatureType() {
      const geojson = this.importedGeojson;
      if (geojson && geojson.features && geojson.features.length) {
        const { geometry } = geojson.features[0];
        if (geometry && geometry.type) {
          this.form.geom_type.value = geometry.type.toLowerCase();
        } else {
          this.form.geom_type.value = "none";
        }
        this.updateStore(); // register geom_type in store

        // Use a subset of the first N features to build the form
        const subsetFeatures = geojson.features.slice(0, 200); // Adjust '200' based on performance needs
        const propertiesList = subsetFeatures.map(
          (feature) => feature.properties,
        );
        this.buildCustomForm(propertiesList);
      }
      this.setTitleFromFile();
    },

    importCSVFeatureType() {
      const rows = this.importedCsv;
      if (rows && rows.length) {
        this.updateStore(); // register title in store

        // Use a subset of the first N rows to build the form
        const subsetCSV = rows.slice(0, 200); // Adjust '200' based on performance needs
        this.buildCustomForm(subsetCSV);

        const hasCoordinates = rows[0] && "lat" in rows[0] && "lon" in rows[0];
        this.form.geom_type.value = hasCoordinates ? "point" : "none";
        this.updateStore();
      }
      this.setTitleFromFile();
    },

    importJsonFeatureType() {
      const rows = this.importedJson;
      if (rows && rows.length) {
        this.form.geom_type.value = "none"; // JSON are non-geom features
        this.updateStore();

        // Use a subset of the first N objects to build the form
        const subsetJson = rows.slice(0, 200); // Adjust '200' based on performance needs
        this.buildCustomForm(subsetJson);
      }
      this.setTitleFromFile();
    },
  },
};
</script>

<style lang="less">
#app:has(#feature-type-edit) #app-content {
  display: flex;
  flex-direction: column;
  background: #eef0f4;
}
</style>

<style lang="less" scoped>
#feature-type-edit {
  flex: 1 0 auto;
  max-width: none;
  width: 100%;
  margin: 0;
  padding: 1.35rem 1.25rem 2.5rem;
  background: #eef0f4;
  box-sizing: border-box;
}

.ft-create {
  max-width: 980px;
  margin: 0 auto;
  font-size: 14px;
  font-weight: 400;
  line-height: 19.3px;
  letter-spacing: 0.02em;
}

.ft-crumb {
  color: #6b7280;
  margin-bottom: 0.7rem;

  a {
    color: #1a73b8;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

h1 {
  margin: 0 0 1.1rem;
  font-size: 26px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0;
  color: #1c2430;
}

.ft-section {
  background: #fff;
  border: 1px solid #e4e7ee;
  border-radius: 8px;
  padding: 1.25rem 1.4rem 1.4rem;
  margin-top: 1rem;
  box-shadow: 0 1px 2px rgba(20, 30, 50, 0.04);

  h2 {
    margin: 0 0 0.85rem;
    font-size: 18px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0;
    color: #1c2430;
  }
}

.ft-section__intro {
  margin: -0.45rem 0 0.9rem;
  color: #6b7280;
}

.ft-prefill {
  margin: -0.35rem 0 0.75rem;
  color: #1f8a4c;
  font-weight: 600;
}

.ft-general__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.25rem;
}

.ft-field label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.35rem;
  color: #243040;
}

.req {
  color: #d23b3b;
  margin-right: 0.15rem;
}

.ft-field input[type="text"],
.ft-field select {
  width: 100%;
  height: 38px;
  border: 1px solid #cfd3dc;
  border-radius: 4px;
  padding: 0 0.65rem;
  background: #fff;
  font: inherit;
  color: #1c2430;
}

.ft-field select:disabled {
  background: #f4f5f7;
  color: #6b7280;
}

.errorlist {
  margin: 0.35rem 0 0;
  padding: 0;
  list-style: none;
  color: #c0392b;
}

.ft-check {
  display: flex;
  gap: 0.65rem;
  align-items: flex-start;
  margin: 0.9rem 0 0;
  cursor: pointer;

  input {
    -webkit-appearance: none;
    appearance: none;
    position: relative;
    flex: none;
    width: 1.05rem;
    height: 1.05rem;
    margin: 0.2rem 0 0;
    border: 1.5px solid #c5cad3;
    border-radius: 2px;
    background: #fff;
    cursor: pointer;

    &:checked {
      border-color: #b52222;
    }

    &:checked::after {
      content: "";
      position: absolute;
      left: 0.3rem;
      top: 0.08rem;
      width: 0.3rem;
      height: 0.58rem;
      border: solid #b52222;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  strong {
    display: block;
    font-weight: 600;
    color: #1c2430;
  }

  small {
    display: block;
    color: #8b919c;
    margin-top: 0.12rem;
  }
}

.ft-fields {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #e1e4ea;
  border-radius: 6px;
  overflow: hidden;

  th {
    background: #f7f8fa;
    text-align: left;
    font-weight: 600;
    color: #3c4656;
    padding: 0.55rem 0.7rem;
    border-bottom: 1px solid #e1e4ea;
  }
}

.ft-fields__handle {
  width: 2rem;
}

.ft-add-field {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 0.85rem;
  padding: 10px;
  background: #3a468e;
  color: #fff;
  border: 1px solid #3a468e;
  border-radius: 8px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;

  .icon {
    margin: 0;
  }

  &:hover {
    background: #2e3873;
    border-color: #2e3873;
  }
}

.ft-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.85rem;
}

.ft-actions__cancel {
  background: none;
  border: 0;
  color: #3a468e;
  font: inherit;
  cursor: pointer;
  padding: 0.55rem 0.8rem;
}

.ft-actions__import {
  background: #fff;
  color: #3a468e;
  border: 1px solid #3a468e;
  border-radius: 4px;
  padding: 0.55rem 0.9rem;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.ft-actions__primary {
  background: #3a468e;
  color: #fff;
  border: 0;
  border-radius: 4px;
  padding: 0.6rem 1rem;
  font: inherit;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.ft-modal {
  position: fixed;
  inset: 0;
  z-index: 20;
  background: rgba(20, 28, 40, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.ft-modal__card {
  width: 28rem;
  max-width: 100%;
  background: #fff;
  border-radius: 8px;
  padding: 1.25rem 1.3rem 1.1rem;

  h3 {
    margin: 0 0 0.6rem;
  }

  p {
    margin: 0 0 1rem;
  }
}

@media (max-width: 720px) {
  .ft-general__grid {
    grid-template-columns: 1fr;
  }

  .ft-actions {
    flex-wrap: wrap;
  }
}
</style>
